import { ApolloClient, gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { UNITED_STATES } from '@/constants/regions'
import {
  GetAvailableUserDiscountsQuery,
  GetAvailableUserDiscountsQueryVariables,
  GetGiveawayScheduleQuery,
  GetGiveawayScheduleQueryVariables,
  GetHubPageTheatricalReleaseQuery,
  GetHubPageTheatricalReleaseQueryVariables,
  GetLightTheatricalReleaseQuery,
  GetLightTheatricalReleaseQueryVariables,
  GetPublicDiscountsForReleaseQuery,
  GetPublicDiscountsForReleaseQueryVariables,
  GetTheatricalDiscountInfoQuery,
  GetTheatricalDiscountInfoQueryVariables,
  GetTheatricalLandingReleasesQuery,
  GetTheatricalLandingReleasesQueryVariables,
  GetTheatricalReleaseImagesQuery,
  GetTheatricalReleaseImagesQueryVariables,
  GetTheatricalReleaseQuery,
  GetTheatricalReleaseQueryVariables,
  GetTheatricalReleasesForFeatureQuery,
  GetTheatricalReleasesForFeatureQueryVariables,
  GetTheatricalUserPreferredVenuesQuery,
  GetTheatricalUserPreferredVenuesQueryVariables,
  GetUserGuildCodesQuery,
  GetUserGuildCodesQueryVariables,
  TheatricalShowtimeEnhancedDataQuery,
  TheatricalShowtimeEnhancedDataQueryVariables,
  TicketSalesPromosQuery,
  TicketSalesPromosQueryVariables,
  TicketsStatusReference,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { DeepNonNullable } from '@/utils/types'
import { resolveGiveawaySchedule } from './utils'

export const GET_THEATRICAL_DISCOUNT_INFO = gql`
  query getTheatricalDiscountInfo($input: DiscountByCodeInput!) {
    discountByCode(input: $input) {
      discount {
        id
        description
        timesUsed
        name
        type
        value
        theatricalSlug
      }
    }
  }
`

export const GET_THEATRICAL_RELEASES = gql`
  query getTheatricalReleases($input: TheatricalReleasesInput!) {
    theatricalReleases(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        isFeaturedRelease
        title
        region {
          ticketsStatus
        }
      }
    }
  }
`

export const GET_THEATRICAL_RELEASE = gql`
  query getTheatricalRelease($input: TheatricalReleaseInput!, $region: String!) {
    theatricalRelease(input: $input) {
      theatricalRelease {
        id
        movieRating
        catalogContentId
        groupProductVariantId
        unlimitedProductVariantId
        theatricalSlug
        title
        longDescription
        shortDescription
        mediumDescription
        hypebotCode
        verticalPoster
        horizontalPoster
        projectSlug
        soldOutShowtimeCount
        yesterdaySoldTicketCount
        backdropImage
        backdropMobileImage
        logoImage
        logoImageWeb
        critics {
          company
          scores {
            name
            value
            floatValue
          }
          certifiedFresh
          verifiedHot
        }
        theatricalReleaseRegions {
          id
          countryCode
          regionName
          ticketsStatus
        }
        movieDuration
        externalCta {
          id
          title
          subtitle
          ctaText
          ctaUrl
          imageUrl
        }
        region(region: $region) {
          id
          trailerUrl
          seoImage
          releaseDate
          redirectTicketsPage
          ticketsEmergencyRedirectUrl
          freeTicketRequestAmount
          isGroupTicketsEnabled
          ticketsStatus
          trailerThumbnail
          regionName
          countryCode
          useSimpleWaitlistOverlay
          defaultTicketingProviderSlug
          partnerUrl
          faqs {
            question
            answer
          }
        }
        images {
          nonTitleArt {
            image2x3
          }
        }
      }
    }
  }
`

export const GET_LIGHT_THEATRICAL_RELEASE = gql`
  query getLightTheatricalRelease($input: TheatricalReleaseInput!, $region: String!) {
    theatricalRelease(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        title
        projectSlug
        region(region: $region) {
          id
          releaseDate
          ticketsStatus
        }
      }
    }
  }
`

export const GET_HUB_PAGE_THEATRICAL_RELEASE = gql`
  query getHubPageTheatricalRelease($input: TheatricalReleaseInput!, $region: String!) {
    theatricalRelease(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        title
        projectSlug
        region(region: $region) {
          id
          releaseDate
          ticketsStatus
        }
        critics {
          company
          scores {
            name
            value
            floatValue
          }
          certifiedFresh
          verifiedHot
        }
      }
    }
  }
`

export type TheatricalRelease = NonNullable<
  DeepNonNullable<GetTheatricalReleaseQuery, 2>['theatricalRelease']['theatricalRelease']
>

const replacePngWithWebp = (pathOrUrl: string) => {
  return pathOrUrl.replace('.png', '.webp')
}

const tempTransformTheatricalQueryImages = (data: GetTheatricalReleaseQuery) => {
  if (!data?.theatricalRelease?.theatricalRelease) {
    return data
  }

  const originalRelease = data.theatricalRelease.theatricalRelease
  const originalRegion = originalRelease.region

  const transformedRelease = {
    ...originalRelease,
    backdropImage: originalRelease.backdropImage
      ? replacePngWithWebp(originalRelease.backdropImage)
      : originalRelease.backdropImage,
    backdropMobileImage: originalRelease.backdropMobileImage
      ? replacePngWithWebp(originalRelease.backdropMobileImage)
      : originalRelease.backdropMobileImage,
    logoImage: originalRelease.logoImage ? replacePngWithWebp(originalRelease.logoImage) : originalRelease.logoImage,
    logoImageWeb: originalRelease.logoImageWeb
      ? replacePngWithWebp(originalRelease.logoImageWeb)
      : originalRelease.logoImageWeb,
    verticalPoster: originalRelease.verticalPoster
      ? replacePngWithWebp(originalRelease.verticalPoster)
      : originalRelease.verticalPoster,
    horizontalPoster: originalRelease.horizontalPoster
      ? replacePngWithWebp(originalRelease.horizontalPoster)
      : originalRelease.horizontalPoster,
    region: originalRegion
      ? {
          ...originalRegion,
          trailerThumbnail: originalRegion.trailerThumbnail
            ? replacePngWithWebp(originalRegion.trailerThumbnail)
            : originalRegion.trailerThumbnail,
          seoImage: originalRegion.seoImage ? replacePngWithWebp(originalRegion.seoImage) : originalRegion.seoImage,
        }
      : originalRegion,
  }

  return {
    ...data,
    theatricalRelease: {
      ...data.theatricalRelease,
      theatricalRelease: transformedRelease,
    },
  }
}

export type TheatricalReleaseResult = DeepNonNullable<
  GetTheatricalReleaseQuery,
  2
>['theatricalRelease']['theatricalRelease']

export async function getTheatricalRelease({
  theatricalSlug,
  client,
  region,
}: {
  theatricalSlug: string | null
  client: ApolloClient
  region?: string
}): Promise<TheatricalReleaseResult | null> {
  try {
    if (!theatricalSlug) {
      logger().warn('Theatrical slug missing for theatrical release data')
      throw new Error('theatrical slug missing for theatrical release data')
    }
    const { data } = await client.query<GetTheatricalReleaseQuery, GetTheatricalReleaseQueryVariables>({
      query: GET_THEATRICAL_RELEASE,
      variables: {
        input: {
          theatricalSlug,
        },
        region: region?.toUpperCase() ?? UNITED_STATES,
      },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    })
    if (!data) return null
    const transformedData = tempTransformTheatricalQueryImages(data)
    return transformedData?.theatricalRelease?.theatricalRelease ?? null
  } catch (error) {
    logger().error('Fetching Theatrical Release failed', {
      error,
      theatricalSlug,
      context: 'getTheatricalRelease',
    })
    return null
  }
}

export type LightTheatricalReleaseResult = NonNullable<
  GetLightTheatricalReleaseQuery['theatricalRelease']
>['theatricalRelease']

/**
 * Fetches a lightweight theatrical release with only essential fields.
 * Use this instead of getTheatricalRelease when you only need basic info like ticketStatus.
 */
export async function getLightTheatricalRelease({
  theatricalSlug,
  client,
  region,
}: {
  theatricalSlug: string | null
  client: ApolloClient
  region?: string
}): Promise<LightTheatricalReleaseResult | null> {
  try {
    if (!theatricalSlug) {
      return null
    }
    const { data } = await client.query<GetLightTheatricalReleaseQuery, GetLightTheatricalReleaseQueryVariables>({
      query: GET_LIGHT_THEATRICAL_RELEASE,
      variables: {
        input: {
          theatricalSlug,
        },
        region: region?.toUpperCase() ?? UNITED_STATES,
      },
      errorPolicy: 'all',
    })
    return data?.theatricalRelease?.theatricalRelease ?? null
  } catch (error) {
    logger().error(
      'Fetching Light Theatrical Release failed',
      {
        theatricalSlug,
        context: 'getLightTheatricalRelease',
      },
      error,
    )
    return null
  }
}

/**
 * Theatrical release data for the hub page including critics and ticket status.
 * Returns null for shows, licensed content, or when the theatrical release doesn't exist.
 */
export type HubPageTheatricalReleaseResult = NonNullable<
  GetHubPageTheatricalReleaseQuery['theatricalRelease']
>['theatricalRelease']

/**
 * Fetches theatrical release data for the movies hub page with critics and ticket status.
 * Use this instead of useGetTheatricalReleaseBySlug for server-side rendering on movie pages.
 *
 * @returns The theatrical release data, or null if:
 *  - No theatrical slug is provided
 *  - The theatrical release doesn't exist (shows, licensed content)
 *  - The release has an invalid placeholder ID
 */
export async function getHubPageTheatricalRelease({
  theatricalSlug,
  client,
  region,
}: {
  theatricalSlug: string | null
  client: ApolloClient
  region?: string
}): Promise<HubPageTheatricalReleaseResult | null> {
  try {
    if (!theatricalSlug) {
      return null
    }
    const { data } = await client.query<GetHubPageTheatricalReleaseQuery, GetHubPageTheatricalReleaseQueryVariables>({
      query: GET_HUB_PAGE_THEATRICAL_RELEASE,
      variables: {
        input: {
          theatricalSlug,
        },
        region: region?.toUpperCase() ?? UNITED_STATES,
      },
      errorPolicy: 'all',
    })

    const release = data?.theatricalRelease?.theatricalRelease ?? null

    // Filter out invalid/placeholder theatrical releases with all-zeros UUID for licensed titles
    const isValidRelease = release && release.id !== '00000000-0000-0000-0000-000000000000'

    return isValidRelease ? release : null
  } catch (error) {
    logger().error(
      'Fetching Hub Page Theatrical Release failed',
      {
        theatricalSlug,
        context: 'getHubPageTheatricalRelease',
      },
      error,
    )
    return null
  }
}

/**
 * Client-side hook to fetch region-specific ticket status.
 * Use this for personalized ticket availability based on user's detected region.
 * Uses GET_LIGHT_THEATRICAL_RELEASE which includes ticketsStatus.
 *
 * @param theatricalSlug - The theatrical release slug
 * @param region - The user's region code (e.g., 'US', 'UK')
 * @param skip - Whether to skip the query (e.g., for non-movie content)
 * @returns Region-specific ticket status, or null for shows/licensed content
 */
export const useTheatricalTicketStatus = ({
  theatricalSlug,
  region,
  skip = false,
}: {
  theatricalSlug: string | null | undefined
  region: string | null | undefined
  skip?: boolean
}): { ticketStatus: TicketsStatusReference | null; loading: boolean } => {
  const shouldSkip = skip || !theatricalSlug || !region

  const { data, loading } = useQuery<GetLightTheatricalReleaseQuery, GetLightTheatricalReleaseQueryVariables>(
    GET_LIGHT_THEATRICAL_RELEASE,
    {
      variables: {
        input: { theatricalSlug: theatricalSlug ?? '' },
        region: region?.toUpperCase() ?? UNITED_STATES,
      },
      skip: shouldSkip,
      errorPolicy: 'all',
    },
  )

  const release = data?.theatricalRelease?.theatricalRelease
  const isValidRelease = release && release.id !== '00000000-0000-0000-0000-000000000000'

  return {
    ticketStatus: isValidRelease ? release?.region?.ticketsStatus ?? null : null,
    loading,
  }
}

export const GET_TICKET_SALES_PROMOS = gql`
  query ticketSalesPromos($input: TicketSalesPromosInput!) {
    ticketSalesPromos(input: $input) {
      id
      activePromosList
      sweepstakesSchedule {
        id
        name
        startDate
        endDate
        items {
          ticketType
          entriesPerTicket
          multiplier
          startDate
          endDate
        }
      }
    }
  }
`

export async function getTicketSalesProps({
  theatricalSlug,
  client,
  date,
}: {
  theatricalSlug: string | null
  client: ApolloClient
  date?: string
}) {
  if (!theatricalSlug) {
    throw new Error('theatrical slug missing for theatrial release data')
  }
  const { data } = await client.query<TicketSalesPromosQuery, TicketSalesPromosQueryVariables>({
    query: GET_TICKET_SALES_PROMOS,
    variables: {
      input: {
        projectSlug: theatricalSlug,
        date,
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  return data?.ticketSalesPromos?.sweepstakesSchedule
}

export const GET_AVAILABLE_USER_DISCOUNTS = gql`
  query getAvailableUserDiscounts($input: AvailableUserDiscountsInput!) {
    availableUserDiscounts(input: $input) {
      discounts {
        id
        name
        code
        category
        remainingUses
        public
      }
    }
  }
`

export async function getAvailableUserDiscounts({
  email,
  theatricalSlug,
  client,
}: {
  email: string
  theatricalSlug: string | null
  client: ApolloClient
}) {
  if (!theatricalSlug) {
    throw new Error('theatrical slug missing for theatrial release data')
  }
  const { data } = await client.query<GetAvailableUserDiscountsQuery, GetAvailableUserDiscountsQueryVariables>({
    query: GET_AVAILABLE_USER_DISCOUNTS,
    variables: {
      input: {
        email,
        theatricalSlug: theatricalSlug,
      },
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  })
  return data?.availableUserDiscounts?.discounts
}

export const GET_THEATRICAL_RELEASE_IMAGES = gql`
  query getTheatricalReleaseImages($theatricalSlug: String!) {
    project(slug: $theatricalSlug) {
      id
      slug
      title {
        ... on ContentDisplayable {
          id
          logoOriginal: image(category: LOGO, aspect: "original") {
            aspect
            category
            cloudinaryPath
          }
          keyArt16x9: image(category: ANGEL_KEY_ART_1, aspect: "16:9") {
            aspect
            category
            cloudinaryPath
          }
          backdrop16x9: image(category: BACKDROP, aspect: "16:9") {
            aspect
            category
            cloudinaryPath
          }
          backdrop9x16: image(category: BACKDROP, aspect: "9:16") {
            aspect
            category
            cloudinaryPath
          }
        }
      }
    }
  }
`

export async function getTheatricalReleaseImages({
  theatricalSlug,
  client,
}: {
  theatricalSlug: string
  client: ApolloClient
}) {
  const { data } = await client.query<GetTheatricalReleaseImagesQuery, GetTheatricalReleaseImagesQueryVariables>({
    query: GET_THEATRICAL_RELEASE_IMAGES,
    variables: { theatricalSlug },
    errorPolicy: 'all',
  })

  return data?.project?.title
}

export const GET_THEATRICAL_LANDING_RELEASES = gql`
  query getTheatricalLandingReleases($input: TheatricalReleasesInput!) {
    theatricalReleases(input: $input) {
      theatricalRelease {
        backdropImage
        catalogContentId
        horizontalPoster
        id
        isFeaturedRelease
        logoImageWeb
        movieRating
        region {
          id
          countryCode
          releaseDate
          ticketsStatus
        }
        shortDescription
        theatricalSlug
        title
        verticalPoster
      }
    }
  }
`

export async function getTheatricalLandingReleases({ client, locale }: { client: ApolloClient; locale?: string }) {
  const { data } = await client.query<GetTheatricalLandingReleasesQuery, GetTheatricalLandingReleasesQueryVariables>({
    query: GET_THEATRICAL_LANDING_RELEASES,
    variables: { input: { language: locale } },
    errorPolicy: 'all',
  })

  return data?.theatricalReleases?.theatricalRelease
}

export const GET_USER_GUILD_CODES = gql`
  query getUserGuildCodes($input: DiscountsListGuildCodesInput!) {
    discountsListGuildCodes(input: $input) {
      discounts {
        id
        code
        name
        theatricalSlug
        public
      }
    }
  }
`

export type UserGuildDiscounts = NonNullable<Awaited<ReturnType<typeof getUserGuildDiscounts>>>

export const getUserGuildDiscounts = async (client: ApolloClient, userId: string | null, theatricalSlug?: string) => {
  try {
    if (!userId) return []

    const { data } = await client.query<GetUserGuildCodesQuery, GetUserGuildCodesQueryVariables>({
      query: GET_USER_GUILD_CODES,
      errorPolicy: 'all',
      variables: {
        input: theatricalSlug ? { theatricalSlug } : {},
      },
    })

    return data?.discountsListGuildCodes?.discounts ?? []
  } catch (err) {
    logger().error('getUserGuildCodes query failed', err)
    return []
  }
}

export const GET_THEATRICAL_RELEASES_FOR_FEATURE = gql`
  query getTheatricalReleasesForFeature($input: TheatricalReleasesInput!) {
    theatricalReleases(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        isFeaturedRelease
        title
        verticalPoster
        releaseDate
        insertedAt
        projectSlug
        updatedAt
        images {
          angelKeyArt1 {
            image16x9
            image2x3
          }
          nonTitleArt {
            image1x1
          }
        }
      }
    }
  }
`

export type EarlyBirdDiscount = NonNullable<Awaited<ReturnType<typeof getEarlyBirdDiscount>>>

export async function getEarlyBirdDiscount(client: ApolloClient, code = 'EARLYBIRD') {
  try {
    const { data } = await client.query<GetTheatricalDiscountInfoQuery, GetTheatricalDiscountInfoQueryVariables>({
      query: GET_THEATRICAL_DISCOUNT_INFO,
      variables: {
        input: { code },
      },
    })

    return data?.discountByCode?.discount
  } catch (err) {
    logger().error('getEarlyBirdDiscount query failed', err)
    return null
  }
}

export const GET_PUBLIC_DISCOUNTS_FOR_RELEASE = gql`
  query getPublicDiscountsForRelease($input: PublicDiscountsForReleaseInput!) {
    publicDiscountsForRelease(input: $input) {
      discounts {
        id
        code
        name
        description
        theatricalSlug
        value
        category
        remainingUses
        type
        timesUsed
        modifierFlags
        activeChildDiscount {
          id
          code
          value
        }
        blackouts {
          dates
          hours
        }
        showtimeValidDateRange {
          lower
          lowerInclusive
          upper
          upperInclusive
        }
      }
    }
  }
`

export type PublicTheatricalDiscount = NonNullable<Awaited<ReturnType<typeof getPublicDiscountsForRelease>>>[number]
export async function getPublicDiscountsForRelease(client: ApolloClient, theatricalSlug: string) {
  try {
    const { data } = await client.query<GetPublicDiscountsForReleaseQuery, GetPublicDiscountsForReleaseQueryVariables>({
      query: GET_PUBLIC_DISCOUNTS_FOR_RELEASE,
      variables: {
        input: { theatricalSlug },
      },
    })

    return data?.publicDiscountsForRelease?.discounts
  } catch (err) {
    logger().error('getPublicDiscountsForRelease query failed', err)
    return null
  }
}

export type ValidActiveChildDiscount = NonNullable<PublicTheatricalDiscount['activeChildDiscount']> & {
  value: number
}
export type ValidEarlyBirdDiscount = Omit<PublicTheatricalDiscount, 'activeChildDiscount' | 'type' | 'category'> & {
  type: 'PERCENTAGE'
  category: 'BUY_EARLY_SAVE_BIG'
  activeChildDiscount: ValidActiveChildDiscount
}
export type ValidNoFeesDiscount = Omit<PublicTheatricalDiscount, 'category'> & {
  category: 'NO_FEES'
}
export type ValidPublicDiscounts = {
  earlyBirdDiscount: ValidEarlyBirdDiscount | null
  noFeesDiscount: ValidNoFeesDiscount | null
}
export async function getValidPublicDiscountsForRelease(
  client: ApolloClient,
  theatricalSlug: string,
): Promise<{ earlyBirdDiscount: ValidEarlyBirdDiscount | null; noFeesDiscount: ValidNoFeesDiscount | null }> {
  const discounts = await getPublicDiscountsForRelease(client, theatricalSlug)
  const earlyBirdDiscount = discounts?.find(
    (discount): discount is ValidEarlyBirdDiscount =>
      discount &&
      discount.category === 'BUY_EARLY_SAVE_BIG' &&
      discount.type === 'PERCENTAGE' &&
      !!discount.activeChildDiscount?.value,
  )
  const noFeesDiscount = discounts?.find(
    (discount): discount is ValidNoFeesDiscount => discount && discount.category === 'NO_FEES',
  )

  return {
    earlyBirdDiscount: earlyBirdDiscount ?? null,
    noFeesDiscount: noFeesDiscount ?? null,
  }
}

export const GET_GIVEAWAY_SCHEDULE = gql`
  query getGiveawaySchedule($input: TicketSalesPromosInput!) {
    ticketSalesPromos(input: $input) {
      sweepstakesSchedule {
        isActive
        name
        endDate
        startDate
        dailyBonusEntries
        minRewardTicketQuantity
        items {
          ticketType
          multiplier
          entriesPerTicket
          startDate
          endDate
          itemType
          quantityMax
          quantityMin
        }
      }
    }
  }
`

export async function getGiveawaySchedule(client: ApolloClient, theatricalSlug: string) {
  const { data } = await client.query<GetGiveawayScheduleQuery, GetGiveawayScheduleQueryVariables>({
    query: GET_GIVEAWAY_SCHEDULE,
    variables: { input: { projectSlug: theatricalSlug } },
  })
  return resolveGiveawaySchedule(data)
}

export type GiveawaySchedule = NonNullable<Awaited<ReturnType<typeof getGiveawaySchedule>>>
export async function getTheatricalReleasesForFeature(
  federationClient: ApolloClient,
  locale: string,
): Promise<GetTheatricalReleasesForFeatureQuery | null> {
  const theatricalReleasesResult = await federationClient
    .query<GetTheatricalReleasesForFeatureQuery, GetTheatricalReleasesForFeatureQueryVariables>({
      query: GET_THEATRICAL_RELEASES_FOR_FEATURE,
      variables: { input: { language: locale } },
      errorPolicy: 'all',
    })
    .catch((error) => {
      logger().error('Error fetching theatrical releases', { locale }, error)
      return null
    })

  return theatricalReleasesResult?.data || null
}

/** A single theatrical release as returned by the getTheatricalReleasesForFeature query. */
export type TheatricalReleaseForFeature = NonNullable<
  NonNullable<GetTheatricalReleasesForFeatureQuery['theatricalReleases']>['theatricalRelease']
>[number]

export const GET_THEATRICAL_SHOWTIME_ENHANCED_DATA = gql`
  query theatricalShowtimeEnhancedData($input: TheatricalShowtimeEnhancedDataInput!) {
    theatricalShowtimeEnhancedData(input: $input) {
      enhancedData {
        id
        hasSignificantPurchaseVelocity
        nearFullCapacity
      }
    }
  }
`

export async function getShowtimeEnhancedData({
  showtimeId,
  client,
}: {
  showtimeId: string | null | undefined
  client: ApolloClient
}): Promise<boolean> {
  try {
    if (!showtimeId) {
      return false
    }

    const { data } = await client.query<
      TheatricalShowtimeEnhancedDataQuery,
      TheatricalShowtimeEnhancedDataQueryVariables
    >({
      query: GET_THEATRICAL_SHOWTIME_ENHANCED_DATA,
      variables: { input: { showtimeIds: [showtimeId] } },
      errorPolicy: 'all',
    })

    const enhancedDataForShowtime = data?.theatricalShowtimeEnhancedData?.enhancedData?.find(
      (enhancedData) => enhancedData?.id === showtimeId,
    )

    if (!enhancedDataForShowtime) {
      return false
    }

    return enhancedDataForShowtime.hasSignificantPurchaseVelocity
  } catch (error) {
    logger().error('Error fetching enhanced showtime data', { showtimeId }, error)
    return false
  }
}

export const GET_THEATRICAL_RELEASES_WITH_CRITICS = gql`
  query getTheatricalReleasesWithCritics($input: TheatricalReleasesInput!) {
    theatricalReleases(input: $input) {
      theatricalRelease {
        id
        theatricalSlug
        projectSlug
        title
        critics {
          company
          scores {
            name
            value
            floatValue
          }
          certifiedFresh
          verifiedHot
        }
      }
    }
  }
`

export const GET_THEATRICAL_USER_PREFERRED_VENUES = gql`
  query getTheatricalUserPreferredVenues {
    theatricalUserPreferences {
      preferredVenues {
        id
        providerId
      }
    }
  }
`

export async function getTheatricalUserPreferredVenues(client: ApolloClient, userId: string | null) {
  if (!userId) return []
  const { data } = await client.query<
    GetTheatricalUserPreferredVenuesQuery,
    GetTheatricalUserPreferredVenuesQueryVariables
  >({
    query: GET_THEATRICAL_USER_PREFERRED_VENUES,
    errorPolicy: 'all',
  })
  return data?.theatricalUserPreferences?.preferredVenues
}
